// Backend session-API client.
//
// Establishes two conventions every online call follows:
//   1. Session-token auth: online participants have no Firebase account, so
//      requests carry the opaque session token, not a Firebase idToken.
//   2. 503 / Retry-After auto-retry (JOLIVE C2 carryover): the online client
//      is exactly the public client that benefits from retrying transient
//      503s. The desktop client does NOT auto-retry; the web client does.
//
// The backend returns the C2 error envelope: { status, message, code?, request_id }.

export const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

const MAX_RETRIES = 3;
const DEFAULT_BACKOFF_MS = 1000;

export class SessionApiError extends Error {
  status: number;
  code?: string;
  requestId?: string;
  constructor(status: number, message: string, code?: string, requestId?: string) {
    super(message);
    this.name = "SessionApiError";
    this.status = status;
    if (code !== undefined) this.code = code;
    if (requestId !== undefined) this.requestId = requestId;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Honor a Retry-After header (seconds) when present; otherwise back off.
function retryDelayMs(res: Response, attempt: number): number {
  const header = res.headers.get("Retry-After");
  if (header) {
    const secs = Number(header);
    if (!Number.isNaN(secs)) return secs * 1000;
  }
  return DEFAULT_BACKOFF_MS * attempt;
}

interface RequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  sessionToken?: string;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, sessionToken } = opts;

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (sessionToken) headers["X-Session-Token"] = sessionToken;

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}${path}`, init);
    } catch {
      // Dropped connection: retry with backoff, then surface a clean error.
      if (attempt < MAX_RETRIES) {
        await sleep(DEFAULT_BACKOFF_MS * attempt);
        continue;
      }
      throw new SessionApiError(0, "Network error. Please check your connection.");
    }

    if (res.ok) {
      return (await res.json()) as T;
    }

    // 503 is the retryable transient status (C2). Retry with Retry-After.
    if (res.status === 503 && attempt < MAX_RETRIES) {
      await sleep(retryDelayMs(res, attempt));
      continue;
    }

    // Any other error: surface the C2 envelope.
    let payload: { message?: string; code?: string; request_id?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // non-JSON error body; fall through with defaults
    }
    throw new SessionApiError(
      res.status,
      payload.message ?? `Request failed (${res.status})`,
      payload.code,
      payload.request_id,
    );
  }

  throw new SessionApiError(503, "Service is busy. Please try again.", undefined, undefined);
}
